import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Version } from "../../models";
import { ActionButton, TableCell, TableRow } from "../PlaceTable/TableStyle";
import dateFormat from "../../utils/date";
import srcSpinner from "../../assets/images/blue-spinner.gif";
import LutronDesignerClientProxy from "../../backend/clients/LutronDesignerClientProxy";
import { useServiceWorkerContext } from "../ServiceWorkerContext/ServiceWorkerContext";
import Modal from "../../ui/Modal/Modal";

type Props = {
  artifactId: string;
  version: Version;
  label: string;
};

const Body = styled.tbody<{ isActive?: boolean }>`
  & > tr {
    padding: 10px 0px;
  }

  & > tr:first-of-type:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  & > tr:first-of-type {
    background: ${({ isActive }) =>
      isActive ? "rgba(0, 0, 0, 0.04)" : "transparent"};
  }

  ${ActionButton} {
    visibility: hidden;
  }

  & > tr:first-of-type ${ActionButton} {
    visibility: ${({ isActive }) => (isActive ? "visible" : "hidden")};
  }

  & > tr:first-of-type:hover ${ActionButton} {
    visibility: visible;
  }
`;

const VersionRow = styled(TableRow)`
  grid-template-columns: 1.4fr repeat(3, 1.4fr);
`;

const Milestone = styled.div`
  width: auto;
  min-width: 100px;
  border-radius: 39px;
  border: 1px solid var(--btn-border);
  font-weight: 600;
  font-size: 12px;
  color: var(--primary-fg);
  text-align: center;
  padding: 5px 0px 0px;
  background: var(--btn-border);
  background: rgb(151 151 151 / 78%);
  font-weight: 100;
  font-size: 16px;
  border: none;
  padding: 5px 5px;
`;

const Loader = styled.img`
  width: 100px;
  height: 100px;
`;

const Center = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const VersionHistoryRow = ({
  artifactId,
  version,
  label,
}: Props): JSX.Element => {
  const sw = useServiceWorkerContext();
  const [openBtnLoader, setOpenBtnLoader] = useState(false);
  const { t } = useTranslation();

  const handleVersionClick = (version: Version) => {
    setOpenBtnLoader(true);
    const openArtifact = {
      artifactID: artifactId,
      etag: "",
      guiGuid: "",
      guiMD5: "",
      lastModified: version.lastModified,
      artifactHref: version.versionHref,
      orchestratorPayload: "",
    };
    LutronDesignerClientProxy.openArtifactRequest(
      sw.waitForInstalled,
      openArtifact,
    );
  };

  return (
    <Body>
      {openBtnLoader && (
        <Modal>
          <Center data-testid="loader-modal">
            <Loader src={srcSpinner} alt={t("loading")} />
          </Center>
        </Modal>
      )}
      <VersionRow data-testid={`version-row[${label}]`}>
        <TableCell hideColumn>{version.versionId || "-"}</TableCell>
        <TableCell>
          {version.artifactTags?.Milestone ? (
            <Milestone>{version.artifactTags?.Milestone}</Milestone>
          ) : (
            "-"
          )}
        </TableCell>
        <TableCell>{version.artifactTags?.Comments || "-"}</TableCell>
        <TableCell hideColumn>{version.modifiedBy || "-"}</TableCell>
        <TableCell>{dateFormat(version.lastModified) || "-"}</TableCell>
        <TableCell>
          <ActionButton
            data-testid={`version-open-btn[${version.versionId}]`}
            onClick={() => handleVersionClick(version)}
          >
            {t("table.actions.download_artifact")}
          </ActionButton>
        </TableCell>
      </VersionRow>
    </Body>
  );
};

export default VersionHistoryRow;
